/// <amd-module name="Wit/Common/WorkItemDialogs/Components/ChangeParentPanel" />
import * as React from "react";
import { getTFSProject } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentProperties } from "VSS/Platform/Layout";
import { Panel } from "VSSUI/Components/Panel/Panel";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { PanelOverlay } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { IDirectoryDropdownItem } from "Wit/DirectoryService/Models";
import { DirectoryDropdown } from "Wit/DirectoryView/Components/DirectoryDropdown";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { WorkItemTrackingDataProvider, WorkItemTypeIconProvider } from "Wit/UI/Data";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { getChangeParentService } from "../Services/ChangeParentService";

export interface IChangeParentPanelProps extends IVssComponentProperties {
    onChangeParent?: (items: INamedFieldDataDictionary[], newParentId: number) => void;
    onDismiss?: () => void;
    currentDirectory?: DirectoryItem;
    directoryDropdownProvider: DirectoryDropdownProvider;
    pageContext: IVssPageContext;
    workItemsToChange: INamedFieldDataDictionary[];
}

export function ChangeParentPanel(props: IChangeParentPanelProps) {
    const projectId = getTFSProject(props.pageContext)?.id ?? "";

    const renderIcon = (workItem: WorkItem, className?: string): JSX.Element => {
        const workItemType = workItem.fields[CoreFieldRefNames.WorkItemType];

        return (
            <div className={className}>
                <WorkItemTypeIconProvider
                    dataProvider={new WorkItemTrackingDataProvider(props.pageContext)}
                    dataSource={{
                        // Fallback to the projectId if we are displaying an imported work item that is new
                        // (and doesn't have a team project yet)
                        projectIdOrName: projectId,
                        workItemTypeName: workItemType
                    }}
                >
                    {icon => <WorkItemTypeIcon data={icon} className="icon-margin font-size-m" />}
                </WorkItemTypeIconProvider>
            </div>
        );
    };

    const changeParent = async () => {
        const newParentId = changeParentService.getNewParentId().value;
        if (props.onChangeParent && (newParentId !== undefined)) {
            props.onChangeParent(changeParentService.getTopLevelWorkItems().value, newParentId);
        }
        onDismiss(props.onDismiss);
    };

    const changeParentService = getChangeParentService(props.pageContext);

    changeParentService.initialize(props.workItemsToChange || [], props.currentDirectory?.id || "");

    return (
        <Observer
            errorText={changeParentService.getErrorText()}
            topLevelWorkItems={changeParentService.getTopLevelWorkItems()}
            newParentId={changeParentService.getNewParentId()}
            isBusy={changeParentService.getIsBusy()}
        >
            {(headerProps: {
                errorText: string;
                topLevelWorkItems: INamedFieldDataDictionary[];
                newParentId: number | undefined;
                isBusy: boolean;
            }) => {
                return (
                    <Panel
                        titleProps={{ text: Resources.ChangeParentOfWorkItems }}
                        onDismiss={() => onDismiss(props.onDismiss)}
                        description={format(Resources.ChangeParentPanelDescription, headerProps.topLevelWorkItems.length)}
                        footerButtonProps={[
                            {
                                text: Resources.Cancel,
                                onClick: () => onDismiss(props.onDismiss)
                            },
                            {
                                text: Resources.ChangeParentOfSelectedWorkItems,
                                onClick: () => changeParent(),
                                primary: true,
                                style: { flexShrink: 1 }, // The resource string is too long for 200%+ reflow
                                disabled: !!headerProps.errorText || headerProps.newParentId === undefined
                            }
                        ]}
                    >
                        {headerProps.isBusy && <PanelOverlay overlayContent={<Spinner />} />}
                        <div className="flex-column flex-grow">
                            {headerProps.errorText && (
                                <MessageCard severity={MessageCardSeverity.Error} className="flex-noshrink margin-bottom-16">
                                    <div className="flex-column font-size-ms">{headerProps.errorText}</div>
                                </MessageCard>
                            )}
                            <DirectoryDropdown
                                selectedItem={props.currentDirectory}
                                itemProvider={props.directoryDropdownProvider}
                                onSelect={(event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<IDirectoryDropdownItem>) => {
                                    changeParentService.setTeamId(item.data?.directory?.id || "");
                                }}
                            />
                            <Observer
                                teamId={changeParentService.getTeamId()}
                                backlogLevelId={changeParentService.getParentBacklogLevelId()}
                                parentWorkItems={changeParentService.getParentWorkItems()}
                            >
                                {(parentPickerProps: { teamId: string; backlogLevelId: string; parentWorkItems: WorkItem[] }) => {
                                    return (
                                        <>
                                            {parentPickerProps.teamId && parentPickerProps.backlogLevelId && (
                                                <Dropdown
                                                    items={parentPickerProps.parentWorkItems.map(
                                                        (value: WorkItem) =>
                                                            ({
                                                                id: value.id.toString(),
                                                                text: value.fields[CoreFieldRefNames.Title],
                                                                data: value,
                                                                iconProps: {
                                                                    render: className => renderIcon(value, className)
                                                                }
                                                            } as IListBoxItem<WorkItem>)
                                                    )}
                                                    onSelect={(event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<WorkItem>) => {
                                                        if (item.data) {
                                                            changeParentService.setNewParentId(item.data.id);
                                                        }
                                                    }}
                                                    placeholder={Resources.ChangeParentSelectNewParent}
                                                    noItemsText={Resources.ChangeParentNoAvailableParents}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            </Observer>
                        </div>
                    </Panel>
                );
            }}
        </Observer>
    );
}

function onDismiss(dismissCallback?: () => void) {
    if (dismissCallback) {
        dismissCallback();
    }
}
